import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TOCService } from '../../../services/toc.service';
import { AuthService } from '../../../services/auth.service';
import { TocTree, TocSection, TocSlide } from '../../../models/toc.model';
import { lessonSlide } from '../../../models/lessonSlide.model';
import { CommonApiService } from '@app/services/common-api.service';
import { SlideTypeLinesService } from '@app/services/slide-type-lines.service';
import { mapApiSlideTypesToOptions } from '@app/constants/sme-slide-type-options';
import { parseTocNoteFromApi } from '@app/utils/toc-notes';
import { promptQuizConfig, quizConfigFromLessonPreferences } from '@app/utils/quiz-config-prompt';
import { QuizConfig } from '@app/models/toc.model';
import { extractApiErrorMessage } from '@app/utils/ai-error-prompt';
import {
  documentStrictMissingPageRefMessage,
  documentStrictSlideNeedsPageRef,
  isDocumentStrictLesson,
  isQuizTocSlide,
} from '@app/utils/primary-source-toc';
import { SMELessonService } from '@app/services/sme-lesson.service';
import { unwrapLessonRow } from '@app/utils/vio-api-response';
import { triggerGeneratedLessonPptDownload } from '@app/utils/lesson-ppt-download';
import { environment } from '@env/environment';
import { scrollToTocSlide, tocSlideDomId } from '@app/utils/toc-slide-scroll';


@Component({
  selector: 'app-toc-editor',
  templateUrl: './toceditor.component.html',
  styleUrls: ['./toceditor.component.scss']
})
export class TOCEditorComponent implements OnInit {

  lessonId!: number;

  userId = this.commonAPI.userInfo.userID; // Replace with actual user ID retrieval logic

  toc: TocTree | null = null;


  backgroundImageName = '';
  linkedToProject = false;
  lessonStatus = 'EMPTY';
  actionBusy = false;
  pptGenBusy = false;


  backgroundPreviewUrl: string | null = null;
  backgroundError = '';

  translateLanguages = [
    'Hindi',
    'Marathi',
    'Hindi with Technical words in English',
    'Marathi with technical words in English'
  ];

  selectedLanguage = '';

  /** Slide layout types (from designer DB, with local fallback). */
  slideTypeOptions: { code: string; label: string }[] = [];

  /** SSE status while TOC is loaded or AI-generated */
  tocLoadStatus = '';
  private savedQuizDefaults: Partial<QuizConfig> = {};
  needsTocGeneration = false;

  get tocGenerating(): boolean {
    return !!this.tocLoadStatus;
  }

  /** Slide to scroll to after returning from slide editor (query param). */
  private pendingScrollSlideId: string | null = null;
  highlightedSlideId: string | null = null;
  readonly isDocumentStrictLesson = isDocumentStrictLesson;
  readonly tocSlideDomId = tocSlideDomId;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tocService: TOCService,
    private AuthService: AuthService,
    private commonAPI: CommonApiService,
    private slideTypeLinesService: SlideTypeLinesService,
    private lessonService: SMELessonService,
  ) { }

  ngOnInit(): void {
    this.lessonId = Number(this.route.snapshot.paramMap.get('lessonid'));

    this.userId = this.AuthService.getUser();
    console.log("TOC Editor init with user id " + this.userId + " and lesson id " + this.lessonId);

    const scrollSlideId = this.route.snapshot.queryParamMap.get('scrollSlideId');
    if (scrollSlideId) {
      this.pendingScrollSlideId = scrollSlideId;
      this.highlightedSlideId = scrollSlideId;
    }

    this.loadSlideTypeOptions();
    this.loadLessonMeta();
  }

  loadLessonMeta(): void {
    this.lessonService.getLessonMeta(this.userId, this.lessonId).subscribe({
      next: (res) => {
        const row = unwrapLessonRow(res) as {
          lessonStatus?: string;
          linkedToProject?: boolean;
          preferences?: Record<string, unknown>;
        } | null;
        this.lessonStatus = String(row?.lessonStatus || 'EMPTY').toUpperCase();
        this.linkedToProject = !!(row?.linkedToProject);
        const prefs = row?.preferences;
        this.savedQuizDefaults = quizConfigFromLessonPreferences(
          prefs && typeof prefs === 'object' ? prefs : undefined,
        );
        this.loadTOC();
      },
      error: () => {
        this.lessonStatus = 'EMPTY';
        this.linkedToProject = false;
        this.savedQuizDefaults = {};
        this.loadTOC();
      },
    });
  }

  loadSlideTypeOptions(): void {
    this.slideTypeLinesService.getslideTypeList().subscribe({
      next: (rows) => {
        this.slideTypeOptions = mapApiSlideTypesToOptions(rows);
      },
      error: () => {
        this.slideTypeOptions = mapApiSlideTypesToOptions([]);
      },
    });
  }

  private scheduleScrollToPendingSlide(): void {
    const slideId = this.pendingScrollSlideId;
    if (!slideId) return;
    this.pendingScrollSlideId = null;
    this.highlightedSlideId = slideId;
    setTimeout(() => scrollToTocSlide(slideId), 50);
  }

  goback() {
    this.router.navigate(['/SME/lesson/meta/lessonid', this.lessonId]);
  }

  loadTOC() {
    if (this.tocGenerating) return;
    const autoGenerate = this.lessonStatus === 'EMPTY';
    const quizConfig = autoGenerate ? this.quizConfigForTocAutoGenerate() : null;
    if (autoGenerate && quizConfig == null) {
      this.needsTocGeneration = true;
      this.toc = { openingNote: '', closingNote: '', sections: [] };
      this.tocLoadStatus = '';
      return;
    }
    this.needsTocGeneration = false;
    this.tocLoadStatus = autoGenerate ? 'Generating table of contents…' : 'Loading TOC…';
    void this.tocService
      .streamGetTOC(
        this.userId,
        this.lessonId,
        (ev) => {
          if (ev.type === 'progress' && ev.message) {
            this.tocLoadStatus = String(ev.message);
          }
          if (ev.type === 'complete' && ev['data']) {
            this.toc = this.normalizeTocFromApi(ev['data']);
            this.backgroundImageName = this.toc.backgroundImage || '';
            this.tocLoadStatus = '';
            this.needsTocGeneration = false;
            this.lessonStatus = 'TOC';
            this.scheduleScrollToPendingSlide();
          }
          if (ev.type === 'error') {
            this.tocLoadStatus = '';
            const msg = String(ev.message || 'Failed to load TOC');
            if (this.isTocMissingMessage(msg)) {
              this.needsTocGeneration = true;
              this.toc = { openingNote: '', closingNote: '', sections: [] };
            } else {
              alert(msg);
            }
          }
        },
        {
          autoGenerate: autoGenerate,
          quizConfig,
        },
      )
      .catch((e: Error) => {
        this.tocLoadStatus = '';
        const msg = e?.message || 'Failed to load TOC';
        if (this.isTocMissingMessage(msg)) {
          this.needsTocGeneration = true;
          this.toc = { openingNote: '', closingNote: '', sections: [] };
        } else {
          alert(msg);
        }
      });
  }

  generateToc(): void {
    if (this.tocGenerating) return;
    const quizConfig = this.quizConfigForTocAutoGenerate(true);
    if (quizConfig == null) return;
    this.needsTocGeneration = false;
    this.tocLoadStatus = 'Generating table of contents…';
    void this.tocService
      .streamGetTOC(
        this.userId,
        this.lessonId,
        (ev) => {
          if (ev.type === 'progress' && ev.message) {
            this.tocLoadStatus = String(ev.message);
          }
          if (ev.type === 'complete' && ev['data']) {
            this.toc = this.normalizeTocFromApi(ev['data']);
            this.backgroundImageName = this.toc.backgroundImage || '';
            this.tocLoadStatus = '';
            this.lessonStatus = 'TOC';
            this.scheduleScrollToPendingSlide();
          }
          if (ev.type === 'error') {
            this.tocLoadStatus = '';
            alert(String(ev.message || 'Failed to generate TOC'));
          }
        },
        { autoGenerate: true, quizConfig },
      )
      .catch((e: Error) => {
        this.tocLoadStatus = '';
        alert(e?.message || 'Failed to generate TOC');
      });
  }

  private isTocMissingMessage(msg: string): boolean {
    const m = msg.toLowerCase();
    return m.includes('generate toc') || m.includes('no table of contents');
  }

  private quizConfigForTocAutoGenerate(interactive = false): QuizConfig | null {
    const partial = quizConfigFromLessonPreferences(
      this.savedQuizDefaults as Record<string, unknown> | undefined,
    );
    if (partial.lessonAssessment !== 'Y') {
      return {
        lessonAssessment: 'N',
        enabled: false,
        questionsPerSection: 0,
        endOfLessonCount: 0,
        simpleCount: 0,
        complexCount: 0,
        appliedFinalCount: 0,
      };
    }
    const hasCounts = (partial.questionsPerSection ?? 0) > 0 || (partial.endOfLessonCount ?? 0) > 0;
    if (hasCounts) {
      return {
        lessonAssessment: 'Y',
        enabled: true,
        questionsPerSection: partial.questionsPerSection ?? 0,
        endOfLessonCount: partial.endOfLessonCount ?? 0,
        simpleCount: partial.simpleCount ?? 0,
        complexCount: partial.complexCount ?? 0,
        appliedFinalCount: partial.appliedFinalCount ?? 0,
      };
    }
    if (!interactive) return null;
    return promptQuizConfig(this.savedQuizDefaults);
  }

  addSection() {
    if (!this.toc) return;
    this.toc.sections.push({
      sectionId: this.uuid(),
      title: 'New Section',
      description: '',
      slides: []
    });
  }

  removeSection(index: number) {
    if (!this.toc) return;
    this.toc.sections.splice(index, 1);
  }
  moveSectionUp(i: number) {
    if (!this.toc) return;
    if (i === 0) return;
    [this.toc.sections[i - 1], this.toc.sections[i]] =
      [this.toc.sections[i], this.toc.sections[i - 1]];
  }

  moveSectionDown(i: number) {
    if (!this.toc) return;
    if (i === this.toc.sections.length - 1) return;
    [this.toc.sections[i + 1], this.toc.sections[i]] =
      [this.toc.sections[i], this.toc.sections[i + 1]];
  }
  addSlide(section: any) {
    section.slides.push({
      slideId: this.uuid(),
      title: 'New Slide',
      description: '',
      sourcePageRef: '',
      slideType: '',
      status: 'PENDING'
    });
  }

  removeSlide(section: any, index: number) {
    section.slides.splice(index, 1);
  }

  moveSlideUp(section: any, i: number) {
    if (i === 0) return;
    [section.slides[i - 1], section.slides[i]] =
      [section.slides[i], section.slides[i - 1]];
  }

  moveSlideDown(section: any, i: number) {
    if (i === section.slides.length - 1) return;
    [section.slides[i + 1], section.slides[i]] =
      [section.slides[i], section.slides[i + 1]];
  }

  /* ---------- Save ---------- */
  private normalizeTocFromApi(raw: unknown): TocTree {
    const d = (raw || {}) as TocTree;
    return {
      ...d,
      openingNote: parseTocNoteFromApi(d.openingNote),
      closingNote: parseTocNoteFromApi(d.closingNote),
      sections: Array.isArray(d.sections) ? d.sections : [],
    };
  }

  canClearLessonContent(): boolean {
    if (this.linkedToProject) return false;
    return this.lessonStatus !== 'EMPTY' || !!(this.toc?.sections?.length);
  }

  clearLessonContent(): void {
    if (this.linkedToProject) {
      alert('This lesson is linked to a creator project. Unlink it before clearing content.');
      return;
    }
    if (
      !window.confirm(
        'Clear lesson content? The lesson record is kept, but TOC, slides, project slides, generated images/audio, references, and PPT files are removed. You can generate a fresh TOC afterward.',
      )
    ) {
      return;
    }
    this.actionBusy = true;
    this.tocService.clearLessonContent(this.userId, this.lessonId).subscribe({
      next: () => {
        this.actionBusy = false;
        this.lessonStatus = 'EMPTY';
        this.toc = { openingNote: '', closingNote: '', sections: [] };
        this.backgroundImageName = '';
        this.backgroundPreviewUrl = null;
        this.needsTocGeneration = true;
        this.router.navigate(['/SME/chat'], {
          queryParams: { lessonId: this.lessonId, generateToc: '1' },
        });
      },
      error: (err: unknown) => {
        this.actionBusy = false;
        alert(extractApiErrorMessage(err, 'Failed to clear lesson content.'));
      },
    });
  }

  deleteLesson(): void {
    if (this.linkedToProject) {
      alert('This lesson is linked to a creator project. Unlink it before deleting.');
      return;
    }
    if (
      !window.confirm(
        'Delete this lesson permanently? This removes the lesson, TOC, all slides, project slides, the upload folder (images/audio), and references. This cannot be undone.',
      )
    ) {
      return;
    }
    this.actionBusy = true;
    this.lessonService.deleteLesson(this.userId, this.lessonId).subscribe({
      next: () => {
        this.actionBusy = false;
        this.router.navigate(['/SME/dashboard']);
      },
      error: (err: unknown) => {
        this.actionBusy = false;
        alert(extractApiErrorMessage(err, 'Failed to delete lesson.'));
      },
    });
  }

  saveTOC() {
    if (!this.toc) return;
    this.tocService.saveTOC(this.userId, this.lessonId, this.toc)
      .subscribe({
        next: (res: unknown) => {
          const warnings = (res as { warnings?: string[] })?.warnings;
          if (warnings?.length) {
            alert(`TOC saved.\n\n${warnings.join('\n')}`);
          } else {
            alert('TOC saved');
          }
        },
        error: () => alert('Failed to save TOC'),
      });
  }

  generatePPT() {
    this.pptGenBusy = true;
    this.tocService.generatePPT(this.userId, this.lessonId).subscribe({
      next: (data: unknown) => {
        this.pptGenBusy = false;
        triggerGeneratedLessonPptDownload(
          this.commonAPI,
          `${environment.baseUrl}/lessonMgmt`,
          this.userId,
          this.lessonId,
          data,
          (ok, message) => alert(message),
        );
      },
      error: (err: unknown) => {
        this.pptGenBusy = false;
        alert(extractApiErrorMessage(err, 'PPT generation failed.'));
      },
    });
  }

  translateAndGenerate() {
    if (!this.selectedLanguage) return;

    this.tocService.translateAndGenerate(
      this.userId,
      this.lessonId,
      this.selectedLanguage
    ).subscribe({
      next: (res: any) => {
        const msg = res?.message || 'Translation + PPT generation started';
        alert(msg);
      },
      error: (err: unknown) => {
        alert(extractApiErrorMessage(err, 'Translate-and-generate is not available yet.'));
      },
    });
  }

  onBackgroundSelected(event: any) {
    const file: File = event.target.files[0];
    if (!file) return;

    // Validation
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg'];
    if (!allowedTypes.includes(file.type)) {
      this.backgroundError = 'Only PNG/JPEG images allowed';
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      this.backgroundError = 'Max file size is 2MB';
      return;
    }

    this.backgroundError = '';

    // Preview
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result as string;
      this.backgroundPreviewUrl = dataUrl;
      this.tocService
        .uploadBackgroundFromDataUrl(this.userId, this.lessonId, file.name, dataUrl)
        .subscribe((res) => {
          this.backgroundImageName = res.fileName;
        });
    };
    reader.readAsDataURL(file);
  }

  removeBackground() {
    this.tocService.deleteBackground(this.lessonId, this.userId)
      .subscribe(() => {
        this.backgroundImageName = '';
        this.backgroundPreviewUrl = null;
      });
  }
  CreateSlideDtl(section: any, slideindex: number) {
    //this.
    this.router.navigate(['/SME/lesson/lessonSlide/lessonid', this.lessonId, 'slideid', section.slides[slideindex].slideId, 'slideTitle', section.slides[slideindex].title]);
  }

  EditSlide(section: any, slideindex: number) {
    if (this.tocGenerating) {
      alert('TOC is loading or generating. Please wait until it completes.');
      return;
    }
    const slide = section.slides[slideindex];
    let allowMissingSourcePageRef = false;
    if (documentStrictSlideNeedsPageRef(this.toc?.lessonContentMode, slide)) {
      if (!window.confirm(documentStrictMissingPageRefMessage(slide.title))) {
        return;
      }
      allowMissingSourcePageRef = true;
    }
    this.router.navigate(
      ['/SME/lesson/lessonSlide/lessonid', this.lessonId, 'slideid', slide.slideId, 'slideTitle', slide.title],
      allowMissingSourcePageRef ? { queryParams: { allowMissingSourcePageRef: '1' } } : {},
    );
    this.highlightedSlideId = slide.slideId;
  }


  getBackgroundUrl(): string {
    return this.backgroundImageName
      ? this.tocService.getBackgroundUrl(this.backgroundImageName, this.userId, this.lessonId)
      : '';
  }
  uuid(): string {
    return 'xxxxxx'.replace(/x/g, () =>
      Math.floor(Math.random() * 16).toString(16)
    );
  }

  isQuizSlide(slide: { contentType?: string; slideType?: string }): boolean {
    return isQuizTocSlide(slide);
  }

}
